import React, { useMemo } from 'react'
import ChangelogList from '../components/ChangelogList'
import ChangelogFilter from '../components/ChangelogFilter'
import { ChangelogData } from '../types'
import { useChangelogData } from '../hooks/useChangelogData'
import { useChangelogFilter } from '../hooks/useChangelogFilter'

interface ChangelogContainerProps {
  endpoint?: string
  data?: ChangelogData
  showFilters?: boolean
  showScrollToVersion?: boolean
  title?: string
  admin?: boolean
}

const ChangelogContainer: React.FC<ChangelogContainerProps> = ({
  endpoint,
  data,
  showFilters = true,
  showScrollToVersion = true,
  title,
  admin = false,
}) => {
  // Use custom hook for data fetching
  const { data: changelogData, loading, error } = useChangelogData({
    endpoint,
    data,
  })

  // Memoize processed data
  const processedData = useMemo(() => {
    if (!changelogData) return { list: [], versions: [], title: '' }

    const versions = changelogData.list.map(({ version }) => ({
      version,
      slug: version.replace(/\./g, ''),
    }))

    return {
      list: changelogData.list,
      versions,
      title: title || changelogData.title,
    }
  }, [changelogData, title])

  // Use custom hook for filtering
  const {
    filter,
    searchTerm,
    filteredData,
    updateFilter,
    updateSearchTerm,
  } = useChangelogFilter({ data: processedData.list, admin })

  if (loading) {
    return (
      <div
        className="changelog-loading p-4 text-center"
        style={{ color: 'var(--changelog-text)' }}
      >
        <div>Loading changelog...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="changelog-error p-4 text-center"
        style={{
          color: 'var(--changelog-tertiary-text)',
          backgroundColor: 'var(--changelog-fix-bg)',
          border: '1px solid var(--changelog-fix-border)',
          borderRadius: '8px',
        }}
      >
        <div>Error loading changelog: {error}</div>
      </div>
    )
  }

  return (
    <div
      className="ChangelogContainer"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <div className="w-full flex flex-col h-full">
        <div className="changelog-wrapper js-changelog flex flex-col h-full">
          <h1
            className="changelog-title text-3xl font-bold text-center my-8 flex-shrink-0"
            style={{ color: 'var(--changelog-text)' }}
          >
            {processedData.title}
          </h1>

          {showFilters && (
            <div className="flex-shrink-0">
              <ChangelogFilter
                versions={processedData.versions}
                filter={filter}
                searchTerm={searchTerm}
                onFilterChange={updateFilter}
                onSearchChange={updateSearchTerm}
                showScrollToVersion={showScrollToVersion}
                showAdminFilter={admin}
              />
            </div>
          )}

          <div
            className="changelog-content flex-1 overflow-y-auto px-4"
            style={{
              maxHeight: 'calc(100vh - 200px)',
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--changelog-border) transparent',
            }}
          >
            <ChangelogList list={filteredData} filter={filter} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangelogContainer
